import React from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import { format } from "date-fns";
import { formatMoney } from "../utils/formatMoney.js";

const statusColor = {
  pending: "warning",
  approved: "success",
  rejected: "error",
};

export default function AdjustmentCard({
  adjustment,
  payeeLabel,
  onApprove,
  onReject,
  actionLoading = false,
  canApprove = true,
}) {
  const isPending = adjustment.status === "pending";
  const payeeDisplay = payeeLabel || adjustment.providerName || null;

  return (
    <Card variant="outlined" sx={{ mb: 2, borderLeft: 4, borderLeftColor: "warning.main" }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" fontFamily="monospace">
              {adjustment.id}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {adjustment.type?.replace(/_/g, " ")} — {formatMoney(adjustment.amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Partner recovery adjustment — does not pay the customer (use Compensation plans for that).
            </Typography>
          </Box>
          <Chip label={adjustment.status} size="small" color={statusColor[adjustment.status] || "default"} />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {adjustment.reason}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Chip label={`Responsibility: ${adjustment.responsibility}`} size="small" variant="outlined" />
          {adjustment.providerRecoveryAmount != null && (
            <Chip
              label={`Partner recovery: ${formatMoney(adjustment.providerRecoveryAmount)}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          {payeeDisplay && (
            <Chip label={`Payee: ${payeeDisplay}`} size="small" variant="outlined" />
          )}
        </Stack>

        {adjustment.approvedAt && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {adjustment.status} by {adjustment.approvedBy || "—"} on{" "}
            {format(new Date(adjustment.approvedAt), "PPp")}
          </Typography>
        )}

        {isPending && canApprove && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={actionLoading}
              onClick={() => onApprove?.(adjustment)}
              sx={{ textTransform: "none" }}
            >
              Approve & notify provider
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              disabled={actionLoading}
              onClick={() => onReject?.(adjustment)}
              sx={{ textTransform: "none" }}
            >
              Reject
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
